const urlencodeFormData = (fd) => new URLSearchParams([...fd]);

class Keycloak {
  constructor(config) {
    this.accessToken = null;
    this.refreshToken = null;
    this.identityToken = null;
    this.config = config;
    this.redirectCallback = config.onRedirect;
    this.authCallback = config.onAuthenticate;
    this.tokenUpdateCallback = config.onTokenUpdate;
    this.loginCallback = config.onLogin;
    this.errCallback = config.onError;
    this.sessionEndWarning = config.sessionEndWarning || 60;
    this.sessionEndingCallback = config.onSessionEnding;
    this.keycloakUrl = `${config.keycloakUrl}/realms/${config.realm}/protocol/openid-connect`;
    this.idpHint = import.meta.env.VITE_IDP_HINT;
  }

  refreshInterval(expiresIn) {
    if (this.config.refreshInterval) {
      return this.config.refreshInterval * 1000;
    } else {
      const interval = (expiresIn - 60) * 1000;
      if (interval <= 0) {
        console.log(`Warning: Invalid Refresh Interval of ${interval} computed for token that expires in ${expiresIn}`);
        return 3600 * 1000; //use default of 60 minutes
      }
      return interval;
    }
  }

  authenticate() {
    const url = `${this.config.keycloakUrl}/realms/${
      this.config.realm
    }/protocol/openid-connect/auth?response_type=code${this.idpHint ? `&kc_idp_hint=${this.idpHint}` : ''}&client_id=${
      this.config.client
    }&scope=openid%20offline_access&redirect_uri=${this.config.redirectUrl}&nocache=${new Date().getTime()}`;
    window.location.href = url;
  }

  checkForSession() {
    const urlParams = new URLSearchParams(window.location.search);
    this.code = urlParams.get('code');
    this.session_state = urlParams.get('session_state');
    if (this.sessionEndingCallback) {
      this.redirectCallback(this.session_state);
    }
    if (this.code && this.session_state) {
      this.codeFlowAuth();
      window.history.pushState(null, null, document.location.pathname);
    }
  }

  fetchToken(formData, isAuthenticated = false) {
    var xhr = new XMLHttpRequest();
    xhr.open('POST', `${this.keycloakUrl}/token`, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    let self = this;
    let resp = null;
    xhr.onload = function () {
      console.log('Keycloak token response status:', xhr.status);
      if (xhr.status !== 200) {
        if (xhr.status === 400) {
          self.accessToken = null;
          self.refreshToken = null;
        }

        try {
          resp = JSON.parse(xhr.responseText);
        } catch {
          resp = {
            error: `Keycloak token request failed with status ${xhr.status}`,
          };
        }

        if (self.errCallback) {
          self.errCallback(resp);
        }
        return;
      }
      const keycloakResp = JSON.parse(xhr.responseText);
      self.accessToken = keycloakResp.access_token;
      if (keycloakResp.refresh_token) {
        self.refreshToken = keycloakResp.refresh_token;
      }
      self.identityToken = keycloakResp.id_token ?? keycloakResp.identity_token ?? null;
      if (self.tokenUpdateCallback) {
        Promise.resolve(
          self.tokenUpdateCallback({
            accessToken: self.accessToken,
            refreshToken: self.refreshToken,
            identityToken: self.identityToken,
          })
        ).catch((err) => {
          console.warn('Unable to persist refreshed Keycloak tokens:', err);
        });
      }
      const remainingTime = keycloakResp.refresh_expires_in;
      if (typeof remainingTime === 'number' && remainingTime <= self.sessionEndWarning) {
        if (self.sessionEndingCallback) {
          self.sessionEndingCallback(remainingTime);
        }
      }
      setTimeout(function () {
        if (self.refreshToken) {
          self.refresh(self.refreshToken);
        }
      }, self.refreshInterval(keycloakResp.expires_in));
      if (self.authCallback) {
        console.log('[Auth Debug] calling authCallback', Boolean(self.authCallback), Boolean(self.accessToken));
        self.authCallback(self.accessToken);
      }
      if (isAuthenticated && self.loginCallback) {
        console.log('[Auth Debug] calling loginCallback');
        self.loginCallback();
      }
    };
    xhr.onerror = function () {
      if (xhr.responseText) {
        self.errCallback(JSON.parse(xhr.responseText));
      } else {
        self.errCallback({ error: 'Unable to fetch the token due to a Network Error' });
      }
    };
    xhr.send(urlencodeFormData(formData));
  }

  codeFlowAuth() {
    console.log('fetching token');
    var data = new FormData();
    data.append('code', this.code);
    data.append('grant_type', 'authorization_code');
    data.append('client_id', this.config.client);
    data.append('redirect_uri', this.config.redirectUrl);
    this.fetchToken(data, true);
  }

  refresh(refreshToken) {
    console.log('refreshing token');
    var data = new FormData();
    data.append('refresh_token', refreshToken);
    data.append('grant_type', 'refresh_token');
    data.append('client_id', this.config.client);
    this.fetchToken(data, false);
  }

  getAccessToken() {
    return this.accessToken;
  }

  getRefreshToken() {
    return this.refreshToken;
  }

  restoreTokens({ accessToken, refreshToken, identityToken }) {
    this.accessToken = accessToken ?? null;
    this.refreshToken = refreshToken ?? null;
    this.identityToken = identityToken ?? null;
  }

  async refreshStoredSession() {
    if (!this.refreshToken) {
      throw new Error('No store Keycloak refresh token is available.');
    }

    const data = new URLSearchParams();

    data.append('refresh_token', this.refreshToken);
    data.append('grant_type', 'refresh_token');
    data.append('client_id', this.config.client);

    const response = await fetch(`${this.keycloakUrl}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      throw new Error(error?.error_description ?? error?.error ?? 'Unable to refresh Keycloak session.');
    }

    const result = await response.json();
    this.accessToken = result.access_token;

    if (result.refresh_token) {
      this.refreshToken = result.refresh_token;
    }
    if (result.id_token) {
      this.identityToken = result.id_token;
    }

    return {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
      identityToken: this.identityToken,
    };
  }

  getIdentityToken() {
    return this.identityToken;
  }
}

const tokenToObject = function (token) {
  let base64Url = token.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  let jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
};

export { Keycloak as default, tokenToObject };
